import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

// Club-level data: not bound to a team or season. The server invalidates ['org'] (and
// ['org', 'members']) after every change, so other admins' edits show up live.
export const orgKeys = {
  all: ['org'] as const,
  settings: ['org', 'settings'] as const,
  teams: ['org', 'teams'] as const,
  seasons: ['org', 'seasons'] as const,
  members: ['org', 'members'] as const,
}

export const settingsQuery = queryOptions({
  queryKey: orgKeys.settings,
  queryFn: () => unwrap(api.org.$get()),
})
export const teamsQuery = queryOptions({
  queryKey: orgKeys.teams,
  queryFn: () => unwrap(api.org.teams.$get()),
})
export const seasonsQuery = queryOptions({
  queryKey: orgKeys.seasons,
  queryFn: () => unwrap(api.org.seasons.$get()),
})
export const membersQuery = queryOptions({
  queryKey: orgKeys.members,
  queryFn: () => unwrap(api.org.members.$get()),
})

export type Settings = Awaited<ReturnType<NonNullable<typeof settingsQuery.queryFn>>>
export type TeamRow = Awaited<ReturnType<NonNullable<typeof teamsQuery.queryFn>>>[number]
export type SeasonsData = Awaited<ReturnType<NonNullable<typeof seasonsQuery.queryFn>>>
export type MembersData = Awaited<ReturnType<NonNullable<typeof membersQuery.queryFn>>>
export type Member = MembersData['members'][number]
export type OpenInvitation = MembersData['invitations'][number]

/** After a club change: settings/teams/seasons and the session (name, colour, teams, seasons). */
export function useOrgRefresh() {
  const qc = useQueryClient()
  return () =>
    Promise.all([qc.invalidateQueries({ queryKey: orgKeys.all }), qc.invalidateQueries({ queryKey: ['session'] })])
}

export function useSeasonAction() {
  const refresh = useOrgRefresh()
  return useMutation({
    mutationFn: ({ key, action }: { key: string; action: 'close' | 'reopen' }) =>
      unwrap(
        action === 'close'
          ? api.org.seasons[':key'].close.$post({ param: { key } })
          : api.org.seasons[':key'].reopen.$post({ param: { key } }),
      ),
    onSuccess: refresh,
  })
}
